import { useEffect, useState } from "react";

const EVENT_OPTIONS = [
  { value: "message.sent", label: "Message Sent" },
  { value: "message.delivered", label: "Message Delivered" },
  { value: "message.read", label: "Message Read" },
  { value: "message.failed", label: "Message Failed" },
  { value: "message.inbound.received", label: "Inbound Message Received" },
  {
    value: "provider.meta.raw",
    label: "Raw Meta Webhook",
    description:
      "Forward original Meta webhook payloads received by XploreByte without an XploreByte envelope.",
  },
];
const EVENT_NAMES = EVENT_OPTIONS.map(option => option.value);

const DEFAULT_EVENT = "message.sent";

export default function CreateWebhookModal({
  isOpen,
  onClose,
  onSubmit,
  busy,
  initialValue,
}) {
  const [url, setUrl] = useState("");
  const [events, setEvents] = useState([DEFAULT_EVENT]);
  const [isActive, setIsActive] = useState(true);

  useEffect(() => {
    if (!isOpen) return;
    const current = initialValue || {};
    const nextEvents =
      Array.isArray(current.events) && current.events.length > 0
        ? current.events.filter(eventName => EVENT_NAMES.includes(eventName))
        : [];
    setUrl(current.url || "");
    setEvents(nextEvents.length > 0 ? nextEvents : [DEFAULT_EVENT]);
    setIsActive(current.isActive !== false);
  }, [isOpen, initialValue]);

  if (!isOpen) return null;

  const toggleEvent = eventName => {
    setEvents(prev =>
      prev.includes(eventName)
        ? prev.filter(item => item !== eventName)
        : [...prev, eventName]
    );
  };

  return (
    <div className="fixed inset-0 z-50 bg-black/40 flex items-center justify-center p-4">
      <div className="w-full max-w-lg rounded-lg bg-white border border-slate-200 shadow-xl">
        <div className="px-5 py-4 border-b border-slate-200">
          <h3 className="text-lg font-semibold text-slate-800">
            {initialValue ? "Update Webhook Endpoint" : "Create Webhook Endpoint"}
          </h3>
        </div>
        <div className="p-5 space-y-4">
          <div>
            <label className="block text-sm font-medium text-slate-700 mb-1">
              URL
            </label>
            <input
              type="url"
              value={url}
              onChange={e => setUrl(e.target.value)}
              className="w-full rounded-md border border-slate-300 px-3 py-2 text-sm"
              placeholder="https://example.com/webhooks/xbytechat"
            />
          </div>
          <div>
            <p className="text-sm font-medium text-slate-700 mb-2">
              Subscribed Events
            </p>
            <div className="grid grid-cols-1 sm:grid-cols-2 gap-2">
              {EVENT_OPTIONS.map(option => (
                <label
                  key={option.value}
                  className="flex items-start gap-2 rounded-md border border-slate-200 p-3 text-sm text-slate-700"
                >
                  <input
                    type="checkbox"
                    className="mt-0.5"
                    checked={events.includes(option.value)}
                    onChange={() => toggleEvent(option.value)}
                  />
                  <span>
                    <span className="block font-medium text-slate-800">
                      {option.label}
                    </span>
                    <code className="text-xs text-slate-500">{option.value}</code>
                    {option.description ? (
                      <span className="mt-1 block text-xs leading-5 text-slate-500">
                        {option.description}
                      </span>
                    ) : null}
                  </span>
                </label>
              ))}
            </div>
          </div>
          <label className="inline-flex items-center gap-2 text-sm text-slate-700">
            <input
              type="checkbox"
              checked={isActive}
              onChange={e => setIsActive(e.target.checked)}
            />
            Active
          </label>
        </div>
        <div className="px-5 py-4 border-t border-slate-200 flex justify-end gap-2">
          <button
            type="button"
            onClick={onClose}
            className="rounded-md border border-slate-300 px-3 py-2 text-sm hover:bg-slate-50"
          >
            Cancel
          </button>
          <button
            type="button"
            disabled={busy || !url.trim() || events.length === 0}
            onClick={() =>
              onSubmit({
                url: url.trim(),
                events,
                isActive,
              })
            }
            className="rounded-md bg-emerald-600 px-3 py-2 text-sm text-white disabled:opacity-60"
          >
            {busy ? "Saving..." : initialValue ? "Update" : "Create"}
          </button>
        </div>
      </div>
    </div>
  );
}
